/**
 * apps/web :: pass :: screens/GeofenceWaitlist
 *
 * The geofence WAITLIST card (ROADS-569 W1.D) — the SP2 validation
 * instrument for the gated native geofence feature (SP3). Mirrors the
 * "Live geofenced zone alerts — notify me" card in the vacationer-experience
 * Pencil design: an eyebrow, a title, a short explainer (buzz before you
 * cross a ZTL line, GPS-only, no data), and a "Notify me when it's live"
 * button.
 *
 * The tap IS the metric. On tap we fire a single `pass.geofence_waitlist.tap`
 * analytics event carrying the active pack's country code, then flip to an
 * inline "You're on the list ✓" confirmation. Frontend-only — no backend
 * route, no email capture, local state only.
 *
 * Wrapped in PassLayout for consistent pass chrome.
 */
import { useState } from "react";

import PassLayout from "../PassLayout";
import { track } from "@/lib/analytics";
import { useActivePassCountries } from "@/lib/pass-content";

export default function GeofenceWaitlist(): JSX.Element {
  const country = useActivePassCountries()[0] ?? "IT";
  const [joined, setJoined] = useState(false);

  function handleNotify(): void {
    track("pass.geofence_waitlist.tap", { country });
    setJoined(true);
  }

  return (
    <PassLayout country={country} title="Live geofenced zone alerts">
      <section
        data-testid="geofence-waitlist-card"
        className="overflow-hidden rounded-2xl bg-indigo-600 text-white"
      >
        <div className="flex items-start gap-3 px-md py-md">
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-xl"
          >
            📡
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-wide text-indigo-300">
              Coming to the app
            </p>
            <h2 className="mt-0.5 font-display text-lg font-bold tracking-tight">
              Live geofenced zone alerts
            </h2>
          </div>
        </div>

        <div className="flex flex-col gap-3 px-md pb-md">
          <p className="text-sm leading-relaxed text-indigo-100">
            Get buzzed before you cross a ZTL line — GPS-only, no data. We're
            building it for the native app. Want it for your trip?
          </p>

          {joined ? (
            <p
              data-testid="geofence-waitlist-confirmation"
              className="inline-flex w-full items-center justify-center rounded-lg bg-white/15 px-4 py-3 text-sm font-bold text-white"
            >
              You're on the list ✓
            </p>
          ) : (
            <button
              type="button"
              data-testid="geofence-waitlist-notify"
              onClick={handleNotify}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-3 text-sm font-bold text-indigo-600 hover:bg-indigo-50"
            >
              <span aria-hidden="true">🔔</span>
              Notify me when it's live
            </button>
          )}
        </div>
      </section>
    </PassLayout>
  );
}
